import { useEffect, useRef } from 'react';

import {
  DiscoveryAssistantMode,
  type AssistantSearchMessage,
  type DiscoveryMessage,
  type DiscoverySession,
} from '@/entities/discovery';
import { useDiscoveryMessagesActions } from '../stores/discovery-message-store';
import { useDiscoveryStore } from '../stores/discovery-store';
import { useExecuteDiscoverySearch } from './use-execute-discovery-search';

export function useExecuteLatestDiscoverySearchMessage({
  messages,
  session,
}: {
  messages: DiscoveryMessage[];
  session: DiscoverySession;
}) {
  const executedOrExecutingMessageIdsRef = useRef(new Set<string>());
  const { mutate: executeSearch } = useExecuteDiscoverySearch();
  const { updateSearchMessageHistoryId } = useDiscoveryMessagesActions();
  const { setSession } = useDiscoveryStore();
  const latestSearchMessage = getLatestSearchMessage(messages);

  useEffect(() => {
    if (!latestSearchMessage) return;
    if (latestSearchMessage.discovery_session_id !== session.session_id) return;

    if (latestSearchMessage.search_history_id) {
      if (session.current_search_history_id !== latestSearchMessage.search_history_id) {
        setSession((prev) => {
          if (!prev || prev.session_id !== session.session_id) return prev;

          return {
            ...prev,
            current_search_history_id: latestSearchMessage.search_history_id,
          };
        });
      }

      return;
    }

    if (executedOrExecutingMessageIdsRef.current.has(latestSearchMessage.message_id)) {
      return;
    }

    executedOrExecutingMessageIdsRef.current.add(latestSearchMessage.message_id);

    executeSearch(
      {
        discoverySessionId: latestSearchMessage.discovery_session_id,
        request: {
          message_id: latestSearchMessage.message_id,
        },
      },
      {
        onSuccess: (response) => {
          updateSearchMessageHistoryId(latestSearchMessage.message_id, response.search_id);
        },
      },
    );
  }, [
    executeSearch,
    latestSearchMessage,
    session.current_search_history_id,
    session.session_id,
    setSession,
    updateSearchMessageHistoryId,
  ]);
}

function getLatestSearchMessage(messages: DiscoveryMessage[]): AssistantSearchMessage | null {
  const latestMessage = messages.reduce<DiscoveryMessage | null>((latest, message) => {
    if (!latest || message.created_at > latest.created_at) {
      return message;
    }

    return latest;
  }, null);

  if (
    latestMessage?.role !== 'assistant' ||
    latestMessage.assistant_mode !== DiscoveryAssistantMode.SEARCH
  ) {
    return null;
  }

  return latestMessage;
}
